import React, { useEffect, useState } from 'react';
import classNames from 'classnames';

import Button from '../../../shared-components/Button';
import { ReactComponent as OfflineGlyph } from '../../../../common/images/offline-local-server.svg';
import styles from './OfflineMode.module.scss';
import { ACTIVE, offlineOrgLabel } from './copy';
import { formatClockTime, formatShortElapsed } from './formatting';
import type { OfflineModeActivation } from 'redux-stores/offlineMode';

export const ELAPSED_TICK_MS = 30_000;

export type ActivePanelProps = {
  organizationName: string;
  activation: OfflineModeActivation;
  isSwitching: boolean;
  onReturn: () => void;
  onSendBroadcast: () => void;
  eventLog?: React.ReactNode;
  broadcast?: React.ReactNode;
};

function useTick(intervalMs: number): number {
  const [now, setNow] = useState(() => Date.now());

  useEffect(() => {
    const id = setInterval(() => setNow(Date.now()), intervalMs);
    return () => clearInterval(id);
  }, [intervalMs]);

  return now;
}

export default function ActivePanel({
  organizationName,
  activation,
  isSwitching,
  onReturn,
  onSendBroadcast,
  eventLog,
  broadcast,
}: ActivePanelProps) {
  const now = useTick(ELAPSED_TICK_MS);
  const offlineOrg = offlineOrgLabel(organizationName);

  const elapsed = formatShortElapsed(activation.updatedAt, now);
  const activatedAt = formatClockTime(activation.updatedAt);
  const actorName = activation.actor?.name;

  return (
    <section className={styles.panel} data-test-id="offlineModeActivePanel">
      <div className={classNames(styles.statusPanel, styles.statusPanelActive)}>
        <span className={styles.iconWell} aria-hidden="true">
          <OfflineGlyph className={styles.iconGlyph} fill="currentColor" />
        </span>

        <div className={styles.statusMain}>
          <h3 className={styles.statusTitle} data-test-id="offlineModeStatus">
            {ACTIVE.status}
          </h3>

          <p className={styles.statusBody} data-test-id="offlineModeOrgName">
            {ACTIVE.body(offlineOrg)}
          </p>

          {activatedAt ? (
            <p className={styles.statusMeta}>
              {'Activated '}
              <span data-test-id="offlineModeActivatedAt">{activatedAt}</span>
              {actorName ? (
                <>
                  {' by '}
                  <span data-test-id="offlineModeActingAdmin">{actorName}</span>
                </>
              ) : null}
              {elapsed ? (
                <>
                  {' · '}
                  <span data-test-id="offlineModeElapsed">{elapsed}</span>
                  {' ago'}
                </>
              ) : null}
            </p>
          ) : null}
        </div>

        <div className={styles.statusActions}>
          <Button
            variant="primary"
            size="medium"
            className={styles.statusActionButton}
            data-test-id="offlineModeSendBroadcastButton"
            disabled={isSwitching}
            onClick={onSendBroadcast}
          >
            {ACTIVE.sendBroadcast}
          </Button>
          <Button
            variant="outlined"
            size="medium"
            className={styles.statusActionButton}
            data-test-id="offlineModeReturnButton"
            disabled={isSwitching}
            onClick={onReturn}
          >
            Return to Cloud Mode
          </Button>
        </div>
      </div>

      {broadcast ? (
        <section className={styles.subSection} data-test-id="offlineModeBroadcastSlot">
          {broadcast}
        </section>
      ) : null}

      {eventLog ? <div data-test-id="offlineModeEventLogSlot">{eventLog}</div> : null}
    </section>
  );
}
